/**
 * useBeforeUnload - Warns the user before leaving the page if there are
 * pending offline items that haven't been synced yet.
 *
 * Listens for the browser 'beforeunload' event and sets event.returnValue
 * to trigger the native "Are you sure you want to leave?" dialog when
 * there are unsynced items in IndexedDB.
 *
 * The warning only fires when pendingCount > 0, so users without pending
 * data can navigate away freely.
 *
 * Usage:
 *	 // Inside a component wrapped by OfflineProvider
 *	 useBeforeUnload();
 *
 * Note: Modern browsers may ignore the custom message string and show
 * their own generic "Changes you made may not be saved" text instead.
 */
import { useEffect } from 'react';
import { useOfflineSync } from './useOfflineSync';

export function useBeforeUnload(): void {
	// Read the current pending count from OfflineContext
	const { pendingCount } = useOfflineSync();

	useEffect(() => {
		/**
		 * beforeunload handler - prevents accidental tab close / navigation
		 * when there are offline items waiting to be synced.
		 *
		 * Setting event.returnValue to a non-empty string is the standard
		 * way to trigger the browser's native confirmation dialog.
		 */
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			// Only warn if there are pending items
			if (pendingCount > 0) {
				// The actual message may be ignored by modern browsers,
				// but setting returnValue is required to trigger the dialog
				const message = 'You have unsynced offline changes. Are you sure you want to leave?';
				event.returnValue = message;
				return message;
			}
		};

		// Attach the listener
		window.addEventListener('beforeunload', handleBeforeUnload);

		// Cleanup: remove the listener when pendingCount changes or unmount
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [pendingCount]);
}
